/**
 * `<DetailedAssessmentSplit>` — nav + detail pane for Detailed Assessment (B-13).
 *
 * Matches prototype `renderAnalysisCards` L784-802 (the Detailed Assessment
 * `.eval-collapsible` block with `.prep-split .eval-split` inner layout).
 *
 * Prototype CSS (applied):
 *   .prep-split { height:420px; background:var(--card); border:1px solid var(--bdr);
 *                 border-radius:10px; overflow:hidden; }  (CollapsibleCard provides this)
 *   .prep-nav { width:190px; flex-shrink:0; border-right:1px solid var(--bdr);
 *               display:flex; flex-direction:column; overflow:hidden; }
 *   .prep-nav-item { display:flex; align-items:center; gap:9px; padding:9px 12px;
 *                    cursor:pointer; border-left:3px solid transparent; transition:all .15s; }
 *   .prep-nav-item:hover { background:rgba(255,255,255,.04); }
 *   .prep-nav-item.active { border-left-color:currentColor; background:rgba(255,255,255,.05); }
 *   .prep-nav-icon { font-size:9px; width:16px; text-align:center; flex-shrink:0; }
 *   .prep-nav-label { font-size:11.5px; font-weight:600; flex:1; white-space:nowrap;
 *                     overflow:hidden; text-overflow:ellipsis; }
 *   .prep-detail { flex:1; overflow-y:auto; padding:18px 20px; }
 *   .prep-detail-lbl { font-size:9px; font-weight:700; text-transform:uppercase;
 *                      letter-spacing:1.1px; margin-bottom:12px; }
 *   .eval-detail-body { font-size:13px; color:var(--txt2); line-height:1.75; }
 *
 * B-13 polish:
 *   - Nav width: 190px (was 200px, off by 10px from proto)
 *   - Nav icon: font-size:9px (was text-xs 12px, off ~3px)
 *   - Nav label: font-size:11.5px font-weight:600 (was text-sm 14px)
 *
 * Mobile breakpoint @media (max-width: 640px): column-stack layout.
 * HoD ruling: exact 640px max-width. Cannot use Tailwind sm: (min-width).
 * We inject a scoped <style> rather than writing flex-col in static CSS.
 *
 * defaultOpen={true} — prototype L791 `▾` = open. Design v2 §1.3 "OK":
 * brief said "collapsed default" but prototype disagrees; prototype wins.
 */
import { useEffect, useState, type JSX } from "react";
import { CollapsibleCard } from "@/components/common/CollapsibleCard";
import { EvaluationMarkdown } from "@/components/candidate-page/EvaluationMarkdown";
import {
  sectionColor,
  shortenSectionLabel,
} from "@/lib/interview-analysis/parsers";
import { NAV_HOVER_BG_04, NAV_ACTIVE_BG_05 } from "@/lib/tokens";

interface DetailedAssessmentSplitProps {
  /**
   * Section key → section body markdown.
   * Keys are the raw headings from parseEvalSections (e.g. "1. The Demo…").
   * The bottom-line / recommendation key is excluded by the caller.
   */
  sections: Record<string, string>;
}

export function DetailedAssessmentSplit({
  sections,
}: DetailedAssessmentSplitProps): JSX.Element | null {
  const navKeys = Object.keys(sections);
  const firstKey = navKeys[0] ?? null;

  const [activeKey, setActiveKey] = useState<string>(firstKey ?? "");

  // Reset active key when sections change (regenerate path).
  useEffect(() => {
    setActiveKey(navKeys[0] ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navKeys.join(",")]);

  if (!navKeys.length) return null;

  const activeBody = sections[activeKey] ?? "";
  const activeColor = sectionColor(activeKey);

  return (
    <CollapsibleCard title="Detailed Assessment" defaultOpen={true}>
      {/*
       * Mobile breakpoint: @media (max-width: 640px) stacks nav above detail.
       * HoD ruling: exact 640px max-width. Cannot use Tailwind sm: (min-width).
       * We inject a scoped <style> rather than writing flex-col in static CSS
       * (which would break the desktop layout).
       */}
      <style>{`
        @media (max-width: 640px) {
          .b13-split-inner { flex-direction: column !important; height: auto !important; }
          .b13-split-nav   { width: 100% !important; border-right: none !important; border-bottom: 1px solid var(--border-default); overflow-y: visible !important; }
          .b13-split-nav ul { flex-direction: row !important; flex-wrap: wrap; overflow-y: visible !important; }
          .b13-split-detail { height: auto; min-height: 200px; }
        }
      `}</style>
      <div
        className="b13-split-inner -mx-3.5 -mb-3 flex overflow-hidden"
        style={{ height: "420px" }}
        data-testid="detailed-assessment-split"
      >
        {/* Left nav — prototype .prep-nav: width:190px (was 200px, off by 10px) */}
        <nav
          className="b13-split-nav flex shrink-0 flex-col overflow-hidden border-r border-border-default"
          style={{ width: "190px" }}
          aria-label="Assessment sections"
        >
          <ul className="flex flex-1 flex-col overflow-y-auto py-1">
            {navKeys.map((key) => {
              const isActive = key === activeKey;
              const color = sectionColor(key);
              const label = shortenSectionLabel(key);
              const numMatch = key.match(/^(\d+)\./);
              // prototype L766: numStr || '◆'
              const icon = numMatch ? numMatch[1] : "◆";
              return (
                <li key={key}>
                  <button
                    type="button"
                    onClick={() => setActiveKey(key)}
                    data-testid="detailed-assessment-nav-item"
                    data-active={isActive}
                    className="flex w-full cursor-pointer items-center gap-[9px] border-l-[3px] px-3 py-[9px] text-left transition-all duration-150"
                    style={{
                      borderLeftColor: isActive ? color : "transparent",
                      background: isActive
                        ? NAV_ACTIVE_BG_05
                        : "transparent",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) {
                        (e.currentTarget as HTMLButtonElement).style.background =
                          NAV_HOVER_BG_04;
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        (e.currentTarget as HTMLButtonElement).style.background =
                          "transparent";
                      }
                    }}
                    aria-current={isActive ? "page" : undefined}
                  >
                    {/* .prep-nav-icon: font-size:9px (was text-xs 12px) */}
                    <span
                      className="inline-flex w-4 shrink-0 items-center justify-center text-center"
                      style={{
                        fontSize: "9px",
                        color: isActive ? color : "var(--text-tertiary)",
                      }}
                      aria-hidden="true"
                    >
                      {icon}
                    </span>
                    {/* .prep-nav-label: font-size:11.5px font-weight:600 (was text-sm 14px) */}
                    <span
                      className="flex-1 truncate"
                      style={{
                        fontSize: "11.5px",
                        fontWeight: 600,
                        color: "var(--text-primary)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {label}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Right detail pane — prototype .prep-detail */}
        <section
          className="b13-split-detail flex-1 overflow-y-auto px-5 py-[18px]"
          data-testid="detailed-assessment-detail"
          style={{
            scrollbarWidth: "thin",
          }}
        >
          {/* .prep-detail-lbl: font-size:9px; font-weight:700; uppercase; letter-spacing:1.1px */}
          <div
            className="mb-3 font-bold uppercase"
            style={{
              color: activeColor,
              fontSize: "9px",
              letterSpacing: "1.1px",
              marginBottom: "12px",
            }}
            data-testid="detailed-assessment-label"
          >
            {activeKey}
          </div>
          {/* EvaluationMarkdown is the canonical AI-markdown renderer — no marked dep. */}
          {activeBody ? (
            <EvaluationMarkdown source={activeBody} />
          ) : null}
        </section>
      </div>
    </CollapsibleCard>
  );
}
